import ErrorBoundary from "@COMPONENTS/common/error/ErrorBoundary";
import {getTrafficIcon, isGoodsTpFH} from "@COMPONENTS/common/eventUtil";
import * as style from "@COMPONENTS/style/partners/stylesM";
import {EventDetail} from "@QUERIES/prdt/event/useEventDetail";
import {format} from "@yb/utils";
import {useMemo} from "react";

const IconListComponent = ({eventDetail}: {eventDetail: EventDetail}) => {
	/** 픽토그램 count */
	const pictogramCount = useMemo(() => {
		let result = 5;
		eventDetail.dayStayInfo && result++;
		["07", "08", "11"].includes(eventDetail.evProgDivi) && result++;
		!isGoodsTpFH(eventDetail.goodsTp) && result++;
		eventDetail.singleChagYn === "Y" && eventDetail.singleChagAmt !== 0 && result++;
		eventDetail.freeScheYn === "Y" && result++;
		return result;
	}, [eventDetail]);
	const {evStaCd, avlilBlockQty} = eventDetail;
	const avlilBlockQtyText = useMemo(() => {
		let result = ``;
		if (evStaCd !== "05") {
			if (avlilBlockQty > 0) {
				result = `잔여 ${avlilBlockQty}석`;
			} else if (avlilBlockQty === 0) {
				result = `잔여석 대기`;
			}
		} else if (avlilBlockQty > 0) {
			result = `잔여 ${avlilBlockQty}석`;
		} else if (avlilBlockQty === 0) {
			result = `잔여석 마감`;
		}
		return result;
	}, [eventDetail]);
	const traffic = getTrafficIcon(eventDetail);

	return (
		<style.ScheduleIconList totalItems={pictogramCount}>
			{/* *박 *일 */}
			{eventDetail.dayStayInfo && (
				<li>
					<style.ScheduleIcon icon="schedule" />
					<span>{eventDetail.dayStayInfo.split("박").join("박 ")}</span>
				</li>
			)}
			{/* 항공편 출/입 */}
			<li>
				<style.ScheduleIcon icon={traffic.icon} />
				{
					<span>
						{traffic.name}
						{traffic.icon === "air-premium" && (
							<>
								<br />
								{eventDetail.seatClassNm}
							</>
						)}
						{eventDetail.stopoverCntYn === "Y" && !(eventDetail.outStovCnt === 0 && eventDetail.inStovCnt === 0) && (
							<>
								<br />- 출국: {eventDetail.outStovCnt > 0 ? `경유 ${eventDetail.outStovCnt}회` : "직항"}
								<br />- 입국: {eventDetail.inStovCnt > 0 ? `경유 ${eventDetail.inStovCnt}회` : "직항"}
							</>
						)}
					</span>
				}
			</li>
			{/* 08:출발예정 ,07: 출발확정, 11: 100%출발확정 */}
			{["07", "08", "11"].includes(eventDetail.evProgDivi) && (
				<li>
					<style.ScheduleIcon icon="decide" />
					<span>{eventDetail.evProgDiviNm}</span>
				</li>
			)}
			{/* 가이드 / 인솔자 동행 */}
			<li>
				<style.ScheduleIcon icon="guide" />
				<span>
					{eventDetail.webGuidConfYn === "Y" && eventDetail.webInsolConfYn === "Y" && (
						<>
							가이드포함
							<br />
							인솔자동행
						</>
					)}
					{eventDetail.webGuidConfYn === "N" && eventDetail.webInsolConfYn === "N" && (
						<>
							가이드불포함
							<br />
							인솔자미동행
						</>
					)}
					{eventDetail.webGuidConfYn === "Y" && eventDetail.webInsolConfYn === "N" && <>가이드포함</>}
					{eventDetail.webGuidConfYn === "N" && eventDetail.webInsolConfYn === "Y" && <>인솔자동행</>}
				</span>
			</li>
			{/* 가이드기사 */}
			<li>
				<style.ScheduleIcon icon="expenses" />
				{eventDetail.guideExpAmt ? (
					<span>
						가이드기사
						<br />
						{eventDetail.expCurrCd} {format.koKRFormatNumber(eventDetail.guideExpAmt)}
					</span>
				) : (
					<span>
						가이드기사
						<br />
						경비없음
					</span>
				)}
			</li>
			{!isGoodsTpFH(eventDetail.goodsTp) && (
				<li>
					<style.ScheduleIcon icon="seat" />
					<span>
						{avlilBlockQtyText}
						<br />
						최소출발
						<br />
						{eventDetail.minCnt}명
					</span>
				</li>
			)}
			{/* 여행자보험 항목 제외. 표준약관 / 특별약관 텍스트 노출 */}
			<li>
				<style.ScheduleIcon icon="insurance" />
				<span>{eventDetail.webCancelCommiYn === "N" ? "특별약관" : "표준약관"}</span>
			</li>
			{eventDetail.singleChagYn === "Y" && eventDetail.singleChagAmt !== 0 && (
				<li>
					<style.ScheduleIcon icon="charge" />
					<span>
						싱글차지
						<br />
						{format.koKRFormatNumber(eventDetail.singleChagAmt)}원
					</span>
				</li>
			)}
			{eventDetail.freeScheYn === "Y" && (
				<li>
					<style.ScheduleIcon icon="free" />
					<span>
						자유일정
						<br />
						있음
					</span>
				</li>
			)}
			<li>
				<style.ScheduleIcon icon="shopping" />
				<span>쇼핑 {eventDetail.shopCnt > 0 ? `${eventDetail.shopCnt}회` : "없음"}</span>
			</li>
		</style.ScheduleIconList>
	);
};

const IconList = (props: {eventDetail: EventDetail}) => (
	<ErrorBoundary>
		<IconListComponent {...props} />
	</ErrorBoundary>
);

const GmarketDetailHeaderComponent = ({eventDetail}: {eventDetail: EventDetail}) => (
	<>
		{eventDetail.noTosYn === "Y" && (
			<style.OptionBox>
				<p>
					모든 경비 포함 <strong>노팁/노옵션/노쇼핑</strong> 상품!
				</p>
			</style.OptionBox>
		)}
		<IconList eventDetail={eventDetail} />
	</>
);

const GmarketDetailHeader = (props: {eventDetail: EventDetail}) => (
	<ErrorBoundary>
		<GmarketDetailHeaderComponent {...props} />
	</ErrorBoundary>
);

export default GmarketDetailHeader;
